import React, { useEffect, useState } from 'react';
import { db, auth } from '../firebase';
import { collection, getDocs, query, orderBy, deleteDoc, doc, getDoc } from 'firebase/firestore';
import { useLocation, Link } from 'react-router-dom';
import ImageModal from '../components/ImageModal'; 
import Sidebar from '../components/Sidebar';
import Header from '../components/Header';
import '../style/products.css';
import { FaTrash, FaShoppingCart } from 'react-icons/fa';
import axios from 'axios';

const Products = () => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const shopId = queryParams.get('shop');

  const [designs, setDesigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);

  // Pagination State
  const [searchTerm, setSearchTerm] = useState('');
  const [visibleDesigns, setVisibleDesigns] = useState(7);
  const designsPerLoad = 7;

  // Modal state for confirmation
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [designToDelete, setDesignToDelete] = useState(null);

  const [showPushModal, setShowPushModal] = useState(false);
  const [designToPush, setDesignToPush] = useState(null);
  
  // Push to store state
  const [pushLoading, setPushLoading] = useState(false);
  const [pushMessage, setPushMessage] = useState('');
  const [pushStatus, setPushStatus] = useState(''); // 'success' or 'error'

  const user = auth.currentUser;
  if (!user) {
    throw new Error("User not logged in");
  }

  // Helper function to determine product type
  const getProductType = (design) => {
    return design.productType || 'tshirt'; // Default to tshirt if not specified
  };

  // Helper function to get print costs based on product type
  const getPrintCosts = (design, variant = null) => {
    const productType = getProductType(design);
    
    if (productType === 'joggers') {
      return {
        front: design.PrintCost474 || variant?.PrintCost474 || 0,
        back: design.PrintCost474 || variant?.PrintCost474 || 0,
        total: design.PrintCost474 || variant?.PrintCost474 || 0
      };
    } else {
      // T-shirt format
      return {
        front: variant?.costs?.front || design.costs?.front || 0,
        back: variant?.costs?.back || design.costs?.back || 0,
        total: (variant?.costs?.front || design.costs?.front || 0) + (variant?.costs?.back || design.costs?.back || 0)
      };
    }
  };

  // Helper function to get base cost
  const getBaseCost = (design, variant = null) => {
    const productType = getProductType(design);
    
    if (productType === 'joggers') {
      return variant?.baseCost570 || design.baseCost570 || 0;
    } else {
      return variant?.baseCost || design.baseCost || 0;
    }
  };

  // Helper function to get product specifications
  const getProductSpecs = (design, variant = null) => {
    const productType = getProductType(design);
    
    if (productType === 'joggers') {
      return variant?.productGSM || design.productGSM || 'N/A';
    } else {
      return variant?.productSpecs || design.productSpecs || 'N/A';
    }
  };

  // Helper function to format size objects
  const formatSize = (sizeObj) => {
    if (!sizeObj || typeof sizeObj !== 'object') return 'N/A';
    const width = Math.floor(sizeObj.width) || 0;
    const height = Math.floor(sizeObj.height) || 0;
    return `${width}×${height}`;
  };

  const getPrintSizes = (design, variant = null) => {
    const productType = getProductType(design);
    const printSizes = variant?.Printsizes || design.Printsizes || {};
    
    if (productType === 'joggers') {
      // For joggers, print sizes are stored in a detailed map with individual print areas
      const frontSizes = printSizes.front || {};
      const backSizes = printSizes.back || {};
      
      return {
        type: 'joggers',
        front: {
          frontLeftFull: frontSizes.frontLeftFull || { height: 0, width: 0 },
          frontLeftLogo: frontSizes.frontLeftLogo || { height: 0, width: 0 },
          frontRightFull: frontSizes.frontRightFull || { height: 0, width: 0 },
          frontRightLogo: frontSizes.frontRightLogo || { height: 0, width: 0 }
        },
        back: {
          backBumArea: backSizes.backBumArea || { height: 0, width: 0 },
          backLeftFull: backSizes.backLeftFull || { height: 0, width: 0 },
          backRightFull: backSizes.backRightFull || { height: 0, width: 0 }
        }
      };
    } else {
      // Regular t-shirt format
      return {
        type: 'regular',
        front: printSizes.front || { height: 0, width: 0 },
        back: printSizes.back || { height: 0, width: 0 }
      };
    }
  };

  useEffect(() => {
    const fetchDesigns = async () => {
      try {
        const designsCollection = collection(db, 'users', user.uid, 'designs');
        const q = query(designsCollection, orderBy('timestamp', 'desc'));
        const designSnapshot = await getDocs(q);
        const designList = designSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setDesigns(designList);
      } catch (error) {
        console.error("Error fetching designs:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchDesigns();
  }, [shopId]);

  const filteredDesigns = designs.filter(design => 
    design.designName.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const currentDesigns = filteredDesigns.slice(0, visibleDesigns);

  const handleLoadMore = () => {
    setVisibleDesigns(prev => prev + designsPerLoad);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setVisibleDesigns(designsPerLoad); // Reset to initial load amount when searching
  };

  const handleImageClick = (imageSrc) => {
    setSelectedImage(imageSrc);
  };

  const handleCloseModal = () => {
    setSelectedImage(null);
  };

  const handleDeleteClick = (designId) => {
    setDesignToDelete(designId);
    setShowDeleteModal(true);
  };

  const handleDelete = async () => {
    try {
      const designDoc = doc(db, 'users', user.uid, 'designs', designToDelete);
      await deleteDoc(designDoc);
      setDesigns(designs.filter(design => design.id !== designToDelete));
    } catch (error) {
      console.error("Error deleting design:", error);
    } finally {
      setShowDeleteModal(false);
      setDesignToDelete(null);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setDesignToDelete(null);
  };

  const handlePushToStoreClick = (design) => {
    setDesignToPush(design);
    setShowPushModal(true);
    // Reset status messages
    setPushMessage('');
    setPushStatus('');
  };

  const handleCancelPush = () => {
    setShowPushModal(false);
    setDesignToPush(null);
  };

  const handlePushToStore = async () => {
    setPushLoading(true);
    setPushMessage('');
    setPushStatus('');

    try {
      // Get the current user's shop domain from URL params or user data
      const userDocRef = doc(db, 'users', user.uid);
      const userDoc = await getDoc(userDocRef);
  
      if (!userDoc.exists()) {
        return;
      }
  
      const userData = userDoc.data();
      const userShop = userData.shop;

      // Prepare the data for the API call
      const pushData = {
        productName: designToPush.productName || designToPush.designName,
        shop: userShop,
        designName: designToPush.designName,
        description: designToPush.description || '',
        sellingPrice: designToPush.sellingPrice,
        status: 'ACTIVE', // or 'DRAFT' based on your preference
        userId: user.uid,
        colorVariants: designToPush.colorVariants.map(variant => ({
          ...variant,
          images: {
            front: variant.images?.front || null,
            back: variant.images?.back || null
          }
        })),
        printMethod: designToPush.printMethod,
        productGSM: designToPush.productGSM,
        frontDesign: designToPush.frontDesign,
        productType: getProductType(designToPush) // Include product type
      };

      console.log('Pushing product to store:', pushData);

      // Make API call to push the product to Shopify
      const response = await axios.post('https://ufb-1.onrender.com/api/push-to-shopify', pushData, {
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (response.status === 201) {
        setPushMessage('Product successfully pushed to your Shopify store!');
        setPushStatus('success');
        
        // Optionally, you can update the design in Firestore to mark it as pushed
        // await updateDoc(doc(db, 'users', user.uid, 'designs', designToPush.id), {
        //   pushedToStore: true,
        //   shopifyProductId: response.data.productId,
        //   pushedAt: new Date()
        // });
        
      } else {
        throw new Error('Failed to push product to store');
      }

    } catch (error) {
      console.error('Error pushing to store:', error);
      
      let errorMessage = 'Failed to push product to store. ';
      
      if (error.response) {
        // Server responded with error status
        errorMessage += error.response.data.message || error.response.data.error || 'Server error occurred.';
      } else if (error.request) {
        // Request was made but no response received
        errorMessage += 'No response from server. Please check your connection.';
      } else {
        // Something else happened
        errorMessage += error.message || 'An unexpected error occurred.';
      }
      
      setPushMessage(errorMessage);
      setPushStatus('error');
    } finally {
      setPushLoading(false);
    }
  };

  // Helper function to format SKUs for display
  const formatSKUs = (skuArray) => {
    if (!skuArray || !Array.isArray(skuArray)) return 'N/A';
    return skuArray.join(', ');
  };

  // Helper function to get all SKUs for a design
  const getAllSKUsForDesign = (design) => {
    if (!design.colorVariants || design.colorVariants.length === 0) {
      return design.skus ? formatSKUs(design.skus) : 'N/A';
    }
    
    const allSKUs = [];
    design.colorVariants.forEach(variant => {
      if (variant.SKUs && Array.isArray(variant.SKUs)) {
        allSKUs.push(...variant.SKUs);
      }
    });
    
    return allSKUs.length > 0 ? allSKUs.join(', ') : 'N/A';
  };

  if (loading) {
    return <div className="loading-message">Loading...</div>;
  }

  return (
    <div className="products-page">
      <Header />
      <div className="products-container">
        <Sidebar />
        <div className="main-content">
          <Link to={'/select-tshirt'} className="create-button">Create Product</Link>
          <div className="search-container">
            <input
              type="text"
              placeholder="Search by design name..."
              value={searchTerm}
              onChange={handleSearchChange}
              className="search-input"
            />
          </div>
            
          {designs.length === 0 ? (
            <p>No designs uploaded yet for this shop.</p>
          ) : (
            <>
              <table className="design-table">
                <thead>
                  <tr>
                    <th>Design Name</th>
                    <th>Description</th>
                    <th>Product Type</th>
                    <th>Variants</th>
                    <th>SKUs</th>
                    <th>Print Method</th>
                    <th>Print Cost</th>
                    <th>Print Sizes</th>
                    <th>Base Cost</th>
                    <th>Selling Price</th>
                    <th>Total Price</th>
                    <th>Product GSM</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {currentDesigns.map(design => {
                    const productType = getProductType(design);
                    
                    // For designs with variants, create a row for each variant
                    if (design.colorVariants && design.colorVariants.length > 0) {
                      return design.colorVariants.map((variant, index) => {
                        const printCosts = getPrintCosts(design, variant);
                        const printSizes = getPrintSizes(design, variant);
                        const baseCost = getBaseCost(design, variant);
                        const productSpecs = getProductSpecs(design, variant);
                        
                        return (
                          <tr key={`${design.id}-${variant.color}`}>
                            {/* Only add these cells on the first row of the design */}
                            {index === 0 && (
                              <>
                                <td rowSpan={design.colorVariants.length}>
                                  {design.designName}
                                </td>
                                <td rowSpan={design.colorVariants.length}>
                                  {design.description}
                                </td>
                                <td rowSpan={design.colorVariants.length}>
                                  <span className={`product-type-badge ${productType}`}>
                                    {productType.charAt(0).toUpperCase() + productType.slice(1)}
                                  </span>
                                </td>
                              </>
                            )}
                            <td>
                              <p><strong>{variant.productName}</strong></p>
                              <p>{variant.color}</p>
                              <img
                                src={variant.images?.front}
                                alt="Front Design"
                                style={{ width: '50px', height: 'auto' }}
                                onClick={() => handleImageClick(variant.images?.front)}
                              />
                              <img
                                src={variant.images?.back}
                                alt="Back Design"
                                style={{ width: '50px', height: 'auto' }}
                                onClick={() => handleImageClick(variant.images?.back)}
                              />
                            </td>

                            <td>
                              <div style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '4px',
                                alignItems: 'flex-start'
                              }}>
                                {variant.SKUs && variant.SKUs.map((sku, index) => (
                                  <div key={index} style={{
                                    padding: '2px 6px',
                                    backgroundColor: '#f3f4f6',
                                    borderRadius: '4px',
                                    color: '#374151',
                                    border: '1px solid #e5e7eb'
                                  }}>
                                    {sku}
                                  </div>
                                ))}
                              </div>
                            </td>

                            {index === 0 && (
                              <td rowSpan={design.colorVariants.length}>
                                {design.printMethod}
                              </td>
                            )}
                            
                            <td>
                              {productType === 'joggers' ? (
                                <p>Print Cost: {design.PrintCost}</p>
                              ) : (
                                <p>
                                  Front Print: {variant.costs?.front || design.costs?.front} <br />
                                  Back Print: {variant.costs?.back || design.costs?.back}
                                </p>
                              )}
                            </td>

                            <td>
                              {printSizes.type === 'joggers' ? (
                                <div style={{ fontSize: '12px' }}>
                                  <div><strong>Front:</strong></div>
                                  <div>• L Full: {formatSize(printSizes.front.frontLeftFull)}</div>
                                  <div>• L Logo: {formatSize(printSizes.front.frontLeftLogo)}</div>
                                  <div>• R Full: {formatSize(printSizes.front.frontRightFull)}</div>
                                  <div>• R Logo: {formatSize(printSizes.front.frontRightLogo)}</div>
                                  
                                  <div><strong>Back:</strong></div>
                                  <div>• Bum: {formatSize(printSizes.back.backBumArea)}</div>
                                  <div>• L Full: {formatSize(printSizes.back.backLeftFull)}</div>
                                  <div>• R Full: {formatSize(printSizes.back.backRightFull)}</div>
                                </div>
                              ) : (
                                <div style={{ fontSize: '12px' }}>
                                  <div>Front: {formatSize(printSizes.front)}</div>
                                  <div>Back: {formatSize(printSizes.back)}</div>
                                </div>
                              )}
                            </td>

                            <td>
                              {productType === 'joggers' ? 
                                (variant.baseCost570 || design.baseCost570) : 
                                (variant.baseCost || design.baseCost)
                              }
                            </td>

                            {index === 0 && (
                              <td rowSpan={design.colorVariants.length}>
                                {design.sellingPrice}
                              </td>
                            )}
                            
                            <td>
                              <p>{variant.totalPrice}</p>
                            </td>

                            <td>
                              {variant.productGSM || design.productGSM}
                            </td>
                            
                            {index === 0 && (
                              <td rowSpan={design.colorVariants.length}>
                                <button 
                                  onClick={() => handlePushToStoreClick(design)} 
                                  className="push-button"
                                  title="Push to Shopify Store"
                                  style={{ marginBottom: '5px' }}
                                >
                                  <FaShoppingCart />
                                </button>
                                <button onClick={() => handleDeleteClick(design.id)} className="delete-button">
                                  <FaTrash />
                                </button>
                              </td>
                            )}
                          </tr>
                        );
                      });
                    } else {
                      // For designs without variants, create a single row
                      const printCosts = getPrintCosts(design);
                      const printSizes = getPrintSizes(design);
                      const baseCost = getBaseCost(design);
                      const productSpecs = getProductSpecs(design);
                      
                      return (
                        <tr key={design.id}>
                          <td>{design.designName}</td>
                          <td>{design.description}</td>
                          <td>
                            <span className={`product-type-badge ${productType}`}>
                              {productType.charAt(0).toUpperCase() + productType.slice(1)}
                            </span>
                          </td>
                          <td>
                            <p>{design.productName}</p>
                            <img
                              src={design.images?.front}
                              alt="Front Design"
                              style={{ width: '50px', height: 'auto' }}
                              onClick={() => handleImageClick(design.images?.front)}
                            />
                            <img
                              src={design.images?.back}
                              alt="Back Design"
                              style={{ width: '50px', height: 'auto' }}
                              onClick={() => handleImageClick(design.images?.back)}
                            />
                          </td>

                          <td>
                            <div style={{ fontSize: '12px', wordBreak: 'break-all' }}>
                              {formatSKUs(design.SKUs)}
                            </div>
                          </td>

                          <td>{design.printMethod}</td>
                          
                          <td>
                            {productType === 'joggers' ? (
                              <p>{design.PrintCost}</p>
                            ) : (
                              <p>Front Print: {design.costs?.front} <br /> Back Print: {design.costs?.back}</p>
                            )}
                          </td>

                          <td>
                            {printSizes.type === 'joggers' ? (
                              <div style={{ fontSize: '12px' }}>
                                <div><strong>Front:</strong></div>
                                <div>• L Full: {formatSize(printSizes.front.frontLeftFull)}</div>
                                <div>• L Logo: {formatSize(printSizes.front.frontLeftLogo)}</div>
                                <div>• R Full: {formatSize(printSizes.front.frontRightFull)}</div>
                                <div>• R Logo: {formatSize(printSizes.front.frontRightLogo)}</div>
                                
                                <div><strong>Back:</strong></div>
                                <div>• Bum: {formatSize(printSizes.back.backBumArea)}</div>
                                <div>• L Full: {formatSize(printSizes.back.backLeftFull)}</div>
                                <div>• R Full: {formatSize(printSizes.back.backRightFull)}</div>
                              </div>
                            ) : (
                              <div style={{ fontSize: '12px' }}>
                                <div>Front: {formatSize(printSizes.front)}</div>
                                <div>Back: {formatSize(printSizes.back)}</div>
                              </div>
                            )}
                          </td>

                          <td>
                            {design.baseCost}
                          </td>
                          
                          <td>{design.sellingPrice}</td>
                          <td>{design.totalPrice}</td>
                          <td>{design.productGSM}</td>
                          <td>
                            <button 
                              onClick={() => handlePushToStoreClick(design)} 
                              className="push-button"
                              title="Push to Shopify Store"
                              style={{ marginBottom: '5px' }}
                            >
                              <FaShoppingCart />
                            </button>

                            <button onClick={() => handleDeleteClick(design.id)} className="delete-button">
                              <FaTrash />
                            </button>
                          </td>
                        </tr>
                      );
                    }
                  })}
                </tbody>
              </table>

              {visibleDesigns < filteredDesigns.length && (
                <div className="load-more-container">
                  <button onClick={handleLoadMore} className="load-more-button">
                    Load More
                  </button>
                </div>
              )}

              <ImageModal imageSrc={selectedImage} onClose={handleCloseModal} />
            </>
          )}
        </div>
      </div>

      {/* Confirmation Modal for Deleting */}
      {showDeleteModal && (
        <div className="modals">
          <div className="modal-contents">
            <h3>Are you sure you want to delete this design?</h3>
            <button onClick={handleDelete} className="yes-button">Yes</button>
            <button onClick={handleCancelDelete} className="no-button">No</button>
          </div>
        </div>
      )}

      {showPushModal && designToPush && (
        <div className="modals">
          <div className="modal-contents push-modal">
            <h3>Push Product to Shopify Store</h3>
            <p>Are you sure you want to push "{designToPush.designName}" to your Shopify store?</p>
            
            {pushMessage && (
              <div className={`push-message ${pushStatus}`}>
                {pushMessage}
              </div>
            )}
            
            <div className="modal-buttons">
              <button 
                onClick={handlePushToStore} 
                className="yes-button"
                disabled={pushLoading || pushStatus === 'success'}
              >
                {pushLoading ? 'Pushing...' : 'Yes, Push to Store'}
              </button>
              <button 
                onClick={handleCancelPush} 
                className="no-button"
                disabled={pushLoading}
              >
                {pushStatus === 'success' ? 'Close' : 'Cancel'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Products;